/**
 * Tasks API
 * CRUD operations for tasks (Aufgaben)
 */

import {handleSupabaseError, supabase} from './client'
import {DEFAULT_ORDER, DEFAULT_TASK, Feedback, ListTask, Order, Task, TaskWithDetails} from '@/types/Order'
import {getRowsFiltered, ListParams, ListResponse} from "@/api/index";
import {AssetFetchParams, AssetFetchResult} from "@/types/Asset";
import {apiClient, t, useGlobalSetting} from "@/lib/utils";

/**
 * Get single task by ID
 */
export async function getTask(orderId: number, taskId: number): Promise<Task | null> {
    const {data, error} = await supabase
        .from('tasks')
        .select(`
      *,
      feedbacks (*)
    `)
        .eq('order_id', orderId)
        .eq('id', taskId)
        .single()

    if (error) {
        if (error.code === 'PGRST116') return null
        handleSupabaseError(error)
    }

    return data
}

/**
 * Get tasks for an order
 */
export async function getTasksForOrder(orderId: number): Promise<Task[]> {
    const {data, error} = await supabase
        .from('tasks')
        .select(`
      *,
      feedbacks (*)
    `)
        .eq('order_id', orderId)
        .order('sequence', {ascending: true})

    if (error) handleSupabaseError(error)
    return data || []
}

/**
 * Get tasks with details (uses view)
 */
export async function getTasksWithDetails(): Promise<TaskWithDetails[]> {
    const {data, error} = await supabase
        .from('tasks_with_details')
        .select('*')
        .order('scheduled_start', {ascending: false})

    if (error) handleSupabaseError(error)
    return data || []
}


export async function getTasksFiltered(params: ListParams): Promise<ListResponse<ListTask>> {
    return getRowsFiltered(params, "tasks", "id, order_id, title, assignee, asset_id, work_group, state, scheduled_start, scheduled_duration");
}


export async function getCompressedTasks(params: AssetFetchParams, baseFilter: Record<string, any>): Promise<AssetFetchResult> {
    const {
        offset = 0,
        limit = 100,
        search = ""
    } = params;

    const {data, error} = await supabase.rpc('filter_rows', {
        p_table: "tasks",
        p_filters: baseFilter,
        p_sort_column: 'order_id',
        p_sort_order: 'asc',
        p_limit: limit,
        p_offset: offset,
        p_select: "id, title, order_id, state",
        p_search: search,
        p_search_columns: ['id', 'order_id', 'title']
    })

    if (error) throw error

    return {
        items: (data?.items || []).map((d: any) => ({
            title: d.order_id,
            subtitle: `Aufgabe: ${d.id} - ${d.title}` || '',
            chip: d.state,
            chip_color: `${d.state.toString().toLowerCase()}_color`,
            order_id: d.order_id,
            id: d.id
        })),
        total: data?.total || 0,
    }
}

/**
 * Get task titles for search/autocomplete
 */
export async function getTaskTitles(): Promise<{ id: number; order_id: number; title: string }[]> {
    const {data, error} = await supabase
        .from('tasks')
        .select('id, order_id, title')
        .order('id', {ascending: false})

    if (error) handleSupabaseError(error)
    return data || []
}

/**
 * Create new task
 */
export async function createTask(task: Partial<Task>): Promise<Task> {
    const {data, error} = await supabase
        .from('tasks')
        .insert(task)
        .select()
        .single()

    if (error) handleSupabaseError(error)
    return data
}

/**
 * Update task
 */
export async function updateTask(id: number, updates: Partial<Task>): Promise<Task> {
    const {data, error} = await supabase
        .from('tasks')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

    if (error) handleSupabaseError(error)
    return data
}


/*
* Update task state
* */

export const updateTaskState = async (record: Task): Promise<Task> => {
    console.log(record.uuid)
    useGlobalSetting().loadingMessage = t("loading.updateTaskState")
    useGlobalSetting().isLoading = true;
    try {
        const response = await apiClient.put(`/updateTaskState/${record.uuid}`, record);
        useGlobalSetting().isLoading = false;
        return response.data
    } catch (error) {
        console.error('Fehler beim Updaten der Aufgabe:', error)
        return DEFAULT_TASK
    }
}

/**
 * Delete task
 */
export async function deleteTask(id: number): Promise<void> {
    const {error} = await supabase
        .from('tasks')
        .delete()
        .eq('id', id)

    if (error) handleSupabaseError(error)
}

/**
 * Check if task can be started (previous tasks completed)
 */
export async function isTaskStartable(taskId: number): Promise<boolean> {
    const {data, error} = await supabase
        .rpc('is_task_startable', {task_id_param: taskId})

    if (error) handleSupabaseError(error)
    return data ?? false
}

// =====================================================
// FEEDBACKS
// =====================================================

/**
 * Add feedback to task
 * hier wird schon das Backend einbezogen, obwohl es mit dem Frondend möglich wäre um zukünfige Sicherheitsfeatures einfacher Umsetzbar zu machen.
 * Alte Variante:
 *     const {data, error} = await supabase
 *         .from('feedbacks')
 *         .insert({...feedback, task_id: taskId})
 *         .select()
 *         .single()
 *
 *     if (error) handleSupabaseError(error)
 *     return data
 */
export async function addFeedback(taskUUID: string, feedbacks: Feedback[]): Promise<void> {
        useGlobalSetting().loadingMessage = t("loading.updateAufgabe")
    useGlobalSetting().isLoading = true;
    try {
        const response = await apiClient.post(`/task/${taskUUID}/feedback`, feedbacks);
        useGlobalSetting().isLoading = false;
        return response.data
    } catch (error) {
        console.error('Fehler beim Updaten der Aufgabe:', error)
    }
}

/**
 * Update feedback
 */
export async function updateFeedback(id: number, updates: Partial<Feedback>): Promise<Feedback> {
    const {data, error} = await supabase
        .from('feedbacks')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

    if (error) handleSupabaseError(error)
    return data
}

/**
 * Delete feedback
 */
export async function deleteFeedback(id: number): Promise<void> {
    const {error} = await supabase
        .from('feedbacks')
        .delete()
        .eq('id', id)

    if (error) handleSupabaseError(error)
}
